// apps/web/src/features/mypage/pages/EditProfilePage.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { withBase } from "@/ui/Header";
import { api } from "@/lib/api";
import { androidPickImage, onAndroidPickImageResult } from "@/lib/androidBridge";
import { notify } from "@/ui/notify";
import { COLOR, FW, BORDER } from "@/ui/tokens";


const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

/** 이미지를 maxSize(px) 이하로 리사이즈 후 JPEG Blob으로 반환 */
function resizeImage(file: File, maxSize = 1024, quality = 0.85): Promise<File> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            let { width, height } = img;
            if (width <= maxSize && height <= maxSize) { resolve(file); return; }
            if (width > height) { height = Math.round(height * (maxSize / width)); width = maxSize; }
            else { width = Math.round(width * (maxSize / height)); height = maxSize; }
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            canvas.getContext("2d")!.drawImage(img, 0, 0, width, height);
            canvas.toBlob(
                (blob) => {
                    if (!blob) { reject(new Error("resize failed")); return; }
                    resolve(new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" }));
                },
                "image/jpeg",
                quality,
            );
        };
        img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("image load failed")); };
        img.src = url;
    });
}

const BASE = {
    W: 1264,
    // 마이페이지와 동일한 좌우 여백으로 통일
    MARGIN_X: 111,

    // 상단 여백을 줄여(큰 해상도에서 하단이 과하게 비는 현상 완화)
    TOP_GAP: 30,

    AVATAR: 350,
    CAMERA: 84,

    LABEL_FZ: 32,

    // 고정 폭 대신 flex 기반으로 동작하도록 기본값만 유지(실제 스타일은 아래에서 flex 처리)
    INPUT_W: 724,
    INPUT_H: 96,
    INPUT_FZ: 28,

    BTN_W: 299,
    BTN_H: 96,
    BTN_FZ: 28,

    HINT_H: 40, // 힌트 고정 높이(레이아웃 안 흔들리게)
    HINT_FZ: 20,

    SAVE_W: 180,
    SAVE_H: 64,
    SAVE_FZ: 26,

    ROW_GAP: 14,
} as const;

function useScale() {
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1.2 });
    return { s, spx: px, sf, sfpx };
}

type MeUser = {
    id: number;
    username: string;
    nickname: string | null;
    avatar_url: string | null;
    created_at: string;
};

type ServerError = { error?: string; message?: string };

export default function EditProfilePage() {
    const nav = useNavigate();
    const { s, spx, sfpx } = useScale();

    const access = localStorage.getItem("bt.access");

    const [me, setMe] = useState<MeUser | null>(null);
    const [nickname, setNickname] = useState("");
    const [origNickname, setOrigNickname] = useState("");

    const [busy, setBusy] = useState(false);
    const [nickChecked, setNickChecked] = useState(false);
    const [nickAvailable, setNickAvailable] = useState<boolean | null>(null);
    const [nickHint, setNickHint] = useState<{ type: "ok" | "err"; text: string } | null>(null);

    const fileRef = useRef<HTMLInputElement | null>(null);

    const [pendingCallbackId, setPendingCallbackId] = useState<string | null>(null);

    // ✅ 임시 아바타(저장 전 미리보기) — Android 브릿지용
    const [pendingAvatar, setPendingAvatar] = useState<{ callbackId: string; tempUrl: string } | null>(null);
    // ✅ 기본 이미지로 변경 대기(저장 전 UI만 반영)
    const [pendingReset, setPendingReset] = useState(false);
    // ✅ 파일 선택 대기(저장 전 UI만 반영) — PC 파일 picker용
    const [pendingFile, setPendingFile] = useState<File | null>(null);
    const [pendingFileUrl, setPendingFileUrl] = useState<string | null>(null);

    useEffect(() => {
        const off = onAndroidPickImageResult((callbackId, payload) => {
            if (!pendingCallbackId || callbackId !== pendingCallbackId) return;

            setPendingCallbackId(null);

            if (!payload.ok) {
                console.warn("pickImage failed:", payload.error);
                return;
            }

            // Case 1: auth-service pick/complete flow — Android uploaded and got a temp_url
            if (payload.temp_url) {
                setPendingAvatar({ callbackId, tempUrl: payload.temp_url });
                setPendingReset(false);
                setPendingFile(null);
                if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
                setPendingFileUrl(null);
                return;
            }

            // Case 2: Android uploaded directly and returned the final avatar_url
            if (payload.avatar_url) {
                setMe((prev) => (prev ? { ...prev, avatar_url: payload.avatar_url! } : prev));
                setPendingReset(false);
                setPendingFile(null);
                if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
                setPendingFileUrl(null);
                try {
                    const raw = localStorage.getItem("bt.user");
                    if (raw) {
                        const u = JSON.parse(raw);
                        u.avatar_url = payload.avatar_url;
                        localStorage.setItem("bt.user", JSON.stringify(u));
                    }
                } catch {}
                return;
            }

            // Case 3: Android passed image as base64 — convert to File and use PC upload path
            if (payload.base64) {
                fetch(payload.base64)
                    .then((r) => r.blob())
                    .then((blob) => {
                        const file = new File([blob], "avatar.jpg", { type: blob.type || "image/jpeg" });
                        if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
                        setPendingFile(file);
                        setPendingFileUrl(URL.createObjectURL(file));
                        setPendingReset(false);
                        setPendingAvatar(null);
                    })
                    .catch(() => console.warn("base64 decode failed"));
                return;
            }

            console.warn("pickImage: no usable payload", payload);
        });

        return off;
    }, [pendingCallbackId]);

    const onPickFile = async () => {
        // Android 브릿지 존재하면 네이티브 픽커 사용
        if (window.Android?.pickImage) {
            // pick/init → backend (Android uploads to innobooktox.com/api/auth/avatar/pick/complete)
            const init = await fetch(`${API_BASE}/auth/avatar/pick/init`, {
                method: "POST",
                headers: { Authorization: `Bearer ${access}`, Accept: "application/json" },
            }).then((r) => r.json());
            const callbackId = init.callback_id as string;

            setPendingCallbackId(callbackId);

            // ✅ 2) 네이티브 픽커 호출
            const ok = androidPickImage(callbackId);
            if (!ok) {
                setPendingCallbackId(null);
            }
            return;
        }

        // PC fallback(원하면 제거 가능)
        fileRef.current?.click();
    };

    useEffect(() => {
        if (!access) return;

        const headers: Record<string, string> = {
            Accept: "application/json",
            Authorization: `Bearer ${access}`,
        };

        (async () => {
            try {
                const r = await fetch(`${API_BASE}/me`, { headers });
                if (!r.ok) return;

                const d = await r.json();
                const u: MeUser | null = d.user || null;
                setMe(u);

                const nn = (u?.nickname || "").trim();
                setOrigNickname(nn);
                setNickname(nn);
            } catch {
                // ignore
            }
        })();
    }, [access]);

    const avatarSrc = useMemo(() => {
        if (pendingReset) return withBase("icons/profile_default.svg");
        if (pendingFileUrl) return pendingFileUrl;
        const raw = pendingAvatar?.tempUrl || me?.avatar_url || "";
        if (raw) {
            if (raw.startsWith("http")) return raw;
            return `${API_BASE}${raw}`;
        }
        return withBase("icons/profile_default.svg");
    }, [pendingReset, pendingFileUrl, pendingAvatar?.tempUrl, me?.avatar_url]);

    const nicknameDirty = useMemo(() => nickname.trim() !== origNickname.trim(), [nickname, origNickname]);

    // 닉네임 변경 시 중복확인 상태 초기화
    useEffect(() => {
        setNickChecked(false);
        setNickAvailable(null);
        setNickHint(null);
    }, [nickname]);

    const avatarDirty = !!pendingAvatar || pendingReset || !!pendingFile;

    const canSave = useMemo(() => {
        const nicknameOk = !nicknameDirty || (nickChecked && nickAvailable === true);
        const somethingChanged = nicknameDirty || avatarDirty;
        return somethingChanged && nicknameOk;
    }, [nicknameDirty, nickChecked, nickAvailable, avatarDirty]);

    if (!access) {
        nav("/mypage/need-login", { replace: true });
        return null;
    }

    const checkNickname = async () => {
        const nn = nickname.trim();
        if (!nn) {
            setNickHint({ type: "err", text: "닉네임을 입력해주세요." });
            return;
        }

        setBusy(true);
        setNickHint(null);

        try {
            const r = await fetch(`${API_BASE}/me/nickname/available?nickname=${encodeURIComponent(nn)}`, {
                headers: { Accept: "application/json", Authorization: `Bearer ${access}` },
            });
            const data = (await r.json().catch(() => ({}))) as any;

            if (!r.ok) throw new Error(`HTTP ${r.status}`);

            const ok = !!data?.available;
            setNickChecked(true);
            setNickAvailable(ok);

            if (ok) setNickHint({ type: "ok", text: "사용 가능한 닉네임입니다." });
            else setNickHint({ type: "err", text: "이미 사용 중인 닉네임입니다." });
        } catch {
            setNickChecked(true);
            setNickAvailable(false);
            setNickHint({ type: "err", text: "중복확인에 실패했습니다." });
        } finally {
            setBusy(false);
        }
    };

    const uploadAvatar = async (file: File) => {
        setBusy(true);
        try {
            const resized = await resizeImage(file);
            const fd = new FormData();
            fd.append("file", resized);

            const r = await fetch(`${API_BASE}/me/avatar`, {
                method: "POST",
                headers: { Authorization: `Bearer ${access}` },
                body: fd,
            });

            const data = (await r.json().catch(() => ({}))) as any;
            if (!r.ok) {
                const code = String((data as ServerError)?.error || "");
                throw new Error(code || "UPLOAD_FAILED");
            }

            const avatar_url = String(data.avatar_url || "");
            // 화면 즉시 반영
            setMe((prev) => (prev ? { ...prev, avatar_url } : prev));

            // 로컬 bt.user도 업데이트(다른 화면에서 즉시 반영)
            const raw = localStorage.getItem("bt.user");
            if (raw) {
                try {
                    const u = JSON.parse(raw);
                    u.avatar_url = avatar_url;
                    localStorage.setItem("bt.user", JSON.stringify(u));
                } catch {}
            }
        } finally {
            setBusy(false);
        }
    };

    const resetAvatar = () => {
        if (busy) return;
        setPendingAvatar(null);
        if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
        setPendingFile(null);
        setPendingFileUrl(null);
        setPendingReset(true);
    };

    const onSave = async () => {
        if (!canSave) return;
        setBusy(true);

        let nicknameSaved = false;

        try {
            // 1) 닉네임 저장(변경된 경우만)
            if (nicknameDirty) {
                const r = await fetch(`${API_BASE}/me`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        Accept: "application/json",
                        Authorization: `Bearer ${access}`,
                    },
                    body: JSON.stringify({ nickname: nickname.trim() }),
                });
                if (!r.ok) throw new Error("NICKNAME_SAVE_FAILED");
                nicknameSaved = true;
                setOrigNickname(nickname.trim());
            }

            // 2) 아바타 리셋(기본 이미지로 변경 요청된 경우)
            if (pendingReset) {
                await fetch(`${API_BASE}/auth/avatar/reset`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${access}` },
                });
                setMe((prev) => (prev ? { ...prev, avatar_url: null } : prev));
                setPendingReset(false);
                const rawUser = localStorage.getItem("bt.user");
                if (rawUser) {
                    try {
                        const u = JSON.parse(rawUser);
                        u.avatar_url = null;
                        localStorage.setItem("bt.user", JSON.stringify(u));
                    } catch {}
                }
            }

            // 3) 파일 업로드(PC 파일 picker에서 선택한 경우)
            if (pendingFile) {
                const resized = await resizeImage(pendingFile);
                const fd = new FormData();
                fd.append("file", resized);
                const r = await fetch(`${API_BASE}/me/avatar`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${access}` },
                    body: fd,
                });
                const data = (await r.json().catch(() => ({}))) as any;
                if (!r.ok) throw new Error(String((data as ServerError)?.error || "UPLOAD_FAILED"));
                const avatar_url = String(data.avatar_url || "");
                setMe((prev) => (prev ? { ...prev, avatar_url } : prev));
                if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
                setPendingFile(null);
                setPendingFileUrl(null);
                const rawUser = localStorage.getItem("bt.user");
                if (rawUser) {
                    try { const u = JSON.parse(rawUser); u.avatar_url = avatar_url; localStorage.setItem("bt.user", JSON.stringify(u)); } catch {}
                }
            }

            // 4) 아바타 커밋(선택된 경우만)
            if (pendingAvatar) {
                try {
                    const r = await fetch(`${API_BASE}/auth/avatar/pick/commit`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${access}`,
                        },
                        body: JSON.stringify({ callback_id: pendingAvatar.callbackId }),
                    }).then((res) => res.json());

                    const newUrl = String(r.avatar_url || "");
                    setMe((prev) => (prev ? { ...prev, avatar_url: newUrl } : prev));
                    setPendingAvatar(null);

                    // bt.user 반영
                    const raw = localStorage.getItem("bt.user");
                    if (raw) {
                        try {
                            const u = JSON.parse(raw);
                            u.avatar_url = newUrl;
                            localStorage.setItem("bt.user", JSON.stringify(u));
                        } catch {}
                    }
                } catch (e: any) {
                    // 아바타 저장 실패 - 세션 만료 등
                    console.error("Avatar commit failed:", e);
                    const code = e?.error || e?.message || "";
                    if (code === "SESSION_EXPIRED" || code === "SESSION_NOT_FOUND") {
                        notify("이미지 세션이 만료되었습니다. 다시 선택해주세요.");
                    } else {
                        notify("프로필 이미지 저장에 실패했습니다.");
                    }
                    // 실패한 아바타 초기화
                    setPendingAvatar(null);

                    // 닉네임은 저장됐으면 마이페이지로 이동
                    if (nicknameSaved) {
                        nav("/mypage", { replace: true });
                    }
                    return;
                }
            }

            // 모두 성공
            nav("/mypage", { replace: true });
        } catch (e) {
            console.error("Save failed:", e);
            notify("저장에 실패했습니다.");
        } finally {
            setBusy(false);
        }
    };


    const SaveBtn = (
        <button
            type="button"
            onClick={onSave}
            disabled={!canSave || busy}
            style={{
                width: spx(BASE.SAVE_W),
                height: spx(BASE.SAVE_H),
                borderRadius: s(18),
                background: COLOR.black,
                color: COLOR.white,
                fontSize: sfpx(BASE.SAVE_FZ),
                fontWeight: 900,
                border: "none",
                opacity: (!canSave || busy) ? 0.5 : 1,
            }}
        >
            저장
        </button>
    );

    return (
        <PageShell
            title="내 정보 수정"
            onBack={() => nav(-1)}
            backIcon={withBase("icons/prev1.svg")}
            width={spx(BASE.W)}
            headerRight={SaveBtn}
            bodyStyle={{ paddingLeft: spx(BASE.MARGIN_X), paddingRight: spx(BASE.MARGIN_X) }}
        >
            <div style={{ height: spx(BASE.TOP_GAP) }} />

            {/* 아바타 + 카메라 */}
            <div style={{ display: "grid", placeItems: "center" }}>
                <div style={{ position: "relative", width: spx(BASE.AVATAR), height: spx(BASE.AVATAR) }}>
                    <img
                        src={avatarSrc}
                        alt=""
                        style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "999px",
                            objectFit: "cover",
                            display: "block",
                            background: "#cfe6d5",
                        }}
                    />

                    {/* 딤 오버레이 */}
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            background: "rgba(0,0,0,0.35)", // 0.15~0.35 사이로 조절
                            pointerEvents: "none",          // 클릭/터치 방해하지 않음
                        }}
                    />
                    <button
                        type="button"
                        onClick={onPickFile}
                        style={{
                            position: "absolute",
                            left: "50%",
                            top: "50%",
                            transform: "translate(-50%, -50%)",
                            width: spx(BASE.CAMERA),
                            height: spx(BASE.CAMERA),
                            borderRadius: "999px",
                            border: "none",
                            background: "transparent",
                            padding: 0,
                        }}
                        aria-label="프로필 이미지 변경"
                    >
                        <img
                            src={withBase("icons/camera.webp")}
                            alt=""
                            style={{
                                width: spx(BASE.CAMERA),
                                height: spx(BASE.CAMERA),
                                display: "block",
                                objectFit: "contain",
                            }}
                        />
                    </button>

                    {/* 숨김 파일 input (웹 테스트용) */}
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (!f) return;
                            // UI 미리보기만 반영(저장 시 업로드)
                            if (pendingFileUrl) URL.revokeObjectURL(pendingFileUrl);
                            setPendingFile(f);
                            setPendingFileUrl(URL.createObjectURL(f));
                            setPendingReset(false);
                            setPendingAvatar(null);
                            // 같은 파일 다시 선택 가능하게 reset
                            e.currentTarget.value = "";
                        }}
                    />
                </div>
            </div>

            {/* 기본 이미지로 변경 버튼 */}
            <div style={{ display: "flex", justifyContent: "center", marginTop: spx(16) }}>
                <button
                    type="button"
                    onClick={resetAvatar}
                    disabled={busy}
                    style={{
                        background: "transparent",
                        border: "none",
                        color: "#666",
                        fontSize: sfpx(26),
                        fontWeight: 600,
                        textDecoration: "underline",
                        cursor: busy ? "not-allowed" : "pointer",
                        padding: `${spx(8)} ${spx(16)}`,
                    }}
                >
                    기본 이미지로 변경
                </button>
            </div>

            <div style={{ height: spx(32) }} />

            {/* 닉네임 수정 */}
            <div style={{ fontSize: sfpx(BASE.LABEL_FZ), fontWeight: 900, color: COLOR.text }}>닉네임 수정</div>
            <div style={{ height: spx(14) }} />

            {/* flex 기반으로 작은 화면에서도 자연스럽게 줄어들도록 처리 */}
            <div style={{ display: "flex", alignItems: "center", gap: spx(18) }}>
                <input
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    placeholder="닉네임 입력"
                    style={{
                        flex: 1,
                        minWidth: spx(420),
                        height: spx(BASE.INPUT_H),
                        borderRadius: s(14),
                        border: BORDER.boldDark,
                        padding: `0 ${spx(18)}`,
                        fontSize: sfpx(BASE.INPUT_FZ),
                        fontWeight: 800,
                    }}
                />
                <button
                    type="button"
                    onClick={checkNickname}
                    disabled={busy || !nickname.trim()}
                    style={{
                        width: spx(BASE.BTN_W),
                        height: spx(BASE.BTN_H),
                        borderRadius: s(14),
                        background: "#8c8c8c",
                        color: COLOR.white,
                        border: "none",
                        fontSize: sfpx(BASE.BTN_FZ),
                        fontWeight: 900,
                        opacity: (busy || !nickname.trim()) ? 0.6 : 1,
                    }}
                >
                    중복확인
                </button>
            </div>

            {/* 힌트 영역: 고정 높이로 레이아웃 흔들림 방지 */}
            <div style={{ height: spx(BASE.ROW_GAP) }} />
            <div style={{ height: spx(BASE.HINT_H) }}>
                {nickHint && (
                    <div
                        style={{
                            fontSize: sfpx(BASE.HINT_FZ),
                            fontWeight: FW.extraBold,
                            color: nickHint.type === "err" ? COLOR.error : COLOR.success,
                            lineHeight: 1.2,
                        }}
                    >
                        {nickHint.text}
                    </div>
                )}
            </div>

            <div style={{ height: spx(40) }} />
        </PageShell>
    );
}
